/**
 * Stacking for the notification list.
 *
 * The backend emits one row per event, which is right for a log and wrong for a
 * reader: eight leads landing in the same minute produced eight near-identical
 * rows, and the one payment update underneath them scrolled out of the panel.
 * This collapses a day's worth of the same kind of event into a single row
 * carrying a count, so the panel shows what happened rather than how many times
 * it happened.
 *
 * Grouping is presentation only. Nothing here changes what the server sends, so
 * read state, unread counts and pagination are still the server's view — a
 * stack is a lens over the rows on the page, not a new kind of record. Genuine
 * throttling (a nightly digest instead of eight pushes) has to happen where the
 * events are emitted; this is the half that can be done in the client.
 */

const DEFAULT_LABEL = 'Earlier';

function startOfLocalDay(value) {
  // `new Date(null)` is the epoch, not an invalid date, so a row with no
  // timestamp would otherwise be filed under 01 Jan 1970.
  if (value === null || value === undefined || value === '') return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * "Today" / "Yesterday" / "18 Sep", with the year once the date falls outside
 * the current one. Day boundaries are local, so a notification from 11pm last
 * night reads as Yesterday rather than as a bare timestamp.
 */
export function dayLabel(value, now = Date.now()) {
  const day = startOfLocalDay(value);
  const today = startOfLocalDay(now);
  if (!day || !today) return DEFAULT_LABEL;

  if (day.getTime() >= today.getTime()) return 'Today';

  // Stepping the date rather than subtracting 24h keeps this correct across a
  // DST change, which a millisecond subtraction would get wrong by an hour.
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (day.getTime() === yesterday.getTime()) return 'Yesterday';

  return day.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    ...(day.getFullYear() === today.getFullYear() ? {} : { year: 'numeric' })
  });
}

/**
 * What makes two notifications "the same kind".
 *
 * Event type is the real answer — it is what the rules registry keys on, so two
 * rows sharing one are the same sentence with different nouns. Vertical joins
 * the key so a stack never spans the tabs on the notifications page, and title
 * is the fallback for a row that predates event_type or was written by hand.
 */
export function stackKey(notification) {
  const kind =
    notification?.event_type || notification?.title || `id:${notification?.id ?? 'unknown'}`;
  return `${notification?.vertical || 'general'}|${kind}`;
}

function createdAtMs(notification) {
  const ms = new Date(notification?.created_at).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

/**
 * Buckets notifications by day, then — unless stacking is off — by kind within
 * each day.
 *
 * Input order is preserved: the server decides what "newest" means and sorting
 * again here would only disagree with the page the reader is on. Each group
 * still names its own latest member explicitly, so a list that arrives in an
 * unexpected order does not mislabel the row's timestamp.
 *
 * Returns `[{ key, label, count, unreadCount, groups: [...] }]`, where a group
 * with a single item is the plain row it always was. The two day-level counts
 * describe the list that was passed in, not the day: a caller showing a page of
 * twenty out of a busy day's forty would be reporting its page. That is why the
 * screens label their day headings and leave the totals to the server.
 */
export function groupNotifications(notifications, { now = Date.now(), stack = true } = {}) {
  const list = Array.isArray(notifications) ? notifications : [];
  const days = [];
  const dayByKey = new Map();

  for (const notification of list) {
    if (!notification) continue;

    const day = startOfLocalDay(notification.created_at);
    const dayKey = day ? String(day.getTime()) : DEFAULT_LABEL;

    let bucket = dayByKey.get(dayKey);
    if (!bucket) {
      bucket = {
        key: dayKey,
        label: day ? dayLabel(notification.created_at, now) : DEFAULT_LABEL,
        count: 0,
        unreadCount: 0,
        groups: [],
        groupByKey: new Map()
      };
      dayByKey.set(dayKey, bucket);
      days.push(bucket);
    }

    const isUnread = !notification.read_at;
    bucket.count += 1;
    if (isUnread) bucket.unreadCount += 1;

    // Without stacking every row is its own group, which keeps the rendering
    // path identical whether the reader has the toggle on or off.
    const groupKey = stack
      ? `${dayKey}|${stackKey(notification)}`
      : `${dayKey}|id:${notification.id}`;

    let group = bucket.groupByKey.get(groupKey);
    if (!group) {
      group = {
        key: groupKey,
        vertical: notification.vertical,
        eventType: notification.event_type,
        items: [],
        latest: notification,
        count: 0,
        unreadCount: 0,
        unreadIds: [],
        ids: []
      };
      bucket.groupByKey.set(groupKey, group);
      bucket.groups.push(group);
    }

    group.items.push(notification);
    group.ids.push(notification.id);
    group.count += 1;
    if (isUnread) {
      group.unreadCount += 1;
      group.unreadIds.push(notification.id);
    }
    if (createdAtMs(notification) > createdAtMs(group.latest)) group.latest = notification;
  }

  // The per-day lookup map is scaffolding, not part of the shape callers see.
  return days.map((bucket) => ({
    key: bucket.key,
    label: bucket.label,
    count: bucket.count,
    unreadCount: bucket.unreadCount,
    groups: bucket.groups.map((group) => ({ ...group, isStack: group.count > 1 }))
  }));
}

/**
 * The sub-line under a stacked row.
 *
 * The title is already the shared sentence — "Follow-up overdue" — so what the
 * reader is missing is how many and how recently, not a restatement of the
 * event. Kept as a plain string so the caller decides how it is styled.
 */
export function stackSummary(group) {
  if (!group || group.count <= 1) return '';
  const updates = `${group.count} updates`;
  return group.unreadCount > 0 ? `${updates} · ${group.unreadCount} unread` : updates;
}
